import * as THREE from 'three';

export default function Backdrop({
  hexColor = '#42aad7',
  useBackgroundGradient,
  radius = 500,
}: {
  hexColor?: string;
  useBackgroundGradient?: boolean;
  radius?: number;
}) {
  return (
    <>
      {useBackgroundGradient ? (
        <>
          <BackdropGradient hexColor={hexColor} planePosition="top" radius={radius} />
          <BackdropGradient hexColor={hexColor} planePosition="bottom" radius={radius} />
          <BackdropGradient hexColor={hexColor} planePosition="front" radius={radius} />
          <BackdropGradient hexColor={hexColor} planePosition="back" radius={radius} />
          <BackdropGradient hexColor={hexColor} planePosition="left" radius={radius} />
          <BackdropGradient hexColor={hexColor} planePosition="right" radius={radius} />
        </>
      ) : (
        <>
          <BackdropSolid hexColor={hexColor} planePosition="top" radius={radius} />
          <BackdropSolid hexColor={hexColor} planePosition="bottom" radius={radius} />
          <BackdropSolid hexColor={hexColor} planePosition="front" radius={radius} />
          <BackdropSolid hexColor={hexColor} planePosition="back" radius={radius} />
          <BackdropSolid hexColor={hexColor} planePosition="left" radius={radius} />
          <BackdropSolid hexColor={hexColor} planePosition="right" radius={radius} />
        </>
      )}
    </>
  );
}

function BackdropSolid({
  hexColor,
  planePosition,
  radius = 500,
}: {
  hexColor: string;
  planePosition: 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right';
  radius?: number;
}) {
  let rot: THREE.Euler;
  let pos: THREE.Vector3;
  const planeSize = radius * 4;
  switch (planePosition) {
    case 'top':
      rot = new THREE.Euler(Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, radius, 0);
      break;
    case 'bottom':
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, -radius, 0);
      break;
    case 'back':
      rot = new THREE.Euler(0, 0, 0);
      pos = new THREE.Vector3(0, 0, -radius);
      break;
    case 'front':
      rot = new THREE.Euler(0, Math.PI, 0);
      pos = new THREE.Vector3(0, 0, radius);
      break;
    case 'left':
      rot = new THREE.Euler(0, Math.PI / 2, 0);
      pos = new THREE.Vector3(-radius, 0, 0);
      break;
    case 'right':
      rot = new THREE.Euler(0, -Math.PI / 2, 0);
      pos = new THREE.Vector3(radius, 0, 0);
      break;
    default:
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, 0, -radius);
  }
  return (
    <mesh
      rotation={rot}
      position={pos}
      receiveShadow={false}
      castShadow={false}
    >
      <planeGeometry attach="geometry" args={[planeSize, planeSize]} />
      <meshBasicMaterial
        attach="material"
        color={new THREE.Color(hexColor)}
        side={THREE.FrontSide}
        toneMapped={false}
        depthWrite={true}
        fog={false}
      />
    </mesh>
  );
}

function BackdropGradient({
  hexColor,
  planePosition,
  radius = 500,
}: {
  hexColor: string;
  planePosition: 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right';
  radius?: number;
}) {
  const floorColor = '#EFEFEF';
  const planeSize = radius * 4;
  const gradientTexture = gradTexture([
    [1, 0.7, 0.42, 0],
    [floorColor, floorColor, hexColor, hexColor],
  ]);

  let rot: THREE.Euler;
  let pos: THREE.Vector3;
  let map: THREE.Texture;
  switch (planePosition) {
    case 'top':
      rot = new THREE.Euler(Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, radius, 0);
      map = gradTexture([[1], [hexColor]]);
      break;
    case 'bottom':
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, -radius, 0);
      map = gradTexture([[1], [floorColor]]);
      break;
    case 'back':
      rot = new THREE.Euler(0, 0, 0);
      pos = new THREE.Vector3(0, 0, -radius);
      map = gradientTexture;
      break;
    case 'front':
      rot = new THREE.Euler(0, Math.PI, 0);
      pos = new THREE.Vector3(0, 0, radius);
      map = gradientTexture;
      break;
    case 'left':
      rot = new THREE.Euler(0, Math.PI / 2, 0);
      pos = new THREE.Vector3(-radius, 0, 0);
      map = gradientTexture;
      break;
    case 'right':
      rot = new THREE.Euler(0, -Math.PI / 2, 0);
      pos = new THREE.Vector3(radius, 0, 0);
      map = gradientTexture;
      break;
    default:
      rot = new THREE.Euler(-Math.PI / 2, 0, 0);
      pos = new THREE.Vector3(0, 0, -1000);
      map = gradientTexture;
  }
  return (
    <mesh rotation={rot} position={pos}>
      <planeGeometry attach="geometry" args={[planeSize, planeSize]} />
      <meshBasicMaterial
        attach="material"
        map={map}
        side={THREE.FrontSide}
        depthWrite={true}
        fog={false}
      />
    </mesh>
  );
}

function gradTexture(color: Array<any>) {
  const c = document.createElement('canvas');
  const ct = c.getContext('2d');
  const size = 1024;
  c.width = 16;
  c.height = size;
  if (ct) {
    const gradient = ct.createLinearGradient(0, 0, 0, size);
    let i = color[0].length;
    while (i--) {
      gradient.addColorStop(color[0][i], color[1][i]);
    }
    ct.fillStyle = gradient;
    ct.fillRect(0, 0, 16, size);
  }
  const texture = new THREE.Texture(c);
  texture.needsUpdate = true;
  return texture;
}
